import { lazy, type LazyExoticComponent } from "react";

/**
 * Recovery for the one failure a code-split SPA cannot avoid: a tab left
 * open across a deploy.
 *
 * Every deploy replaces the whole hashed bundle, so the chunk a long-lived
 * tab asks for — `Strategies-A251gejR.js`, say — is gone from origin. The
 * static site's `catchall_document: index.html` answers with the HTML
 * document rather than a 404, and the browser's strict MIME check on module
 * scripts rejects it as "Failed to fetch dynamically imported module". The
 * fix is the only one available to the tab: reload, and pick up the bundle
 * that is actually deployed.
 *
 * A reload that is not spent carefully is worse than the failure it repairs,
 * so the attempt is recorded per route in sessionStorage — per route because
 * a guard cleared by any successful load would reload again on every click
 * at a genuinely broken chunk, and sessionStorage because the record has to
 * survive the reload and must not outlive the tab.
 *
 * Nothing records which page the reload is for: every page has its own URL,
 * and a reload stays on the URL — so it lands on the page the user clicked.
 */

const ATTEMPT_KEY_PREFIX = "optivo-ui-chunk-reload:";

/**
 * Storage throws rather than returning null in Safari's private mode and on
 * an opaque origin. Recovery is a best effort by definition: without a record
 * the worst case is a second reload, which is still better than the blank
 * page, so an unavailable store must never abort the reload itself.
 */
function readStorage(key: string): string | null {
    try {
        return window.sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeStorage(key: string, value: string): void {
    try {
        window.sessionStorage.setItem(key, value);
    } catch {
        // Ignored — see readStorage.
    }
}

function clearStorage(key: string): void {
    try {
        window.sessionStorage.removeItem(key);
    } catch {
        // Ignored — see readStorage.
    }
}

/**
 * Runs a route's dynamic import, reloading once if it fails.
 *
 * Any failure counts, not only the ones whose message names a fetch: the
 * wording differs across browsers, and a phrasing we failed to list would
 * leave the actual bug unfixed on that browser. A module that is broken
 * rather than missing therefore costs one reload — after which the recorded
 * attempt lets the error through to `PageErrorBoundary`, which is where a
 * real bug belongs.
 */
export async function loadRouteModule<T>(name: string, load: () => Promise<T>): Promise<T> {
    const attemptKey = `${ATTEMPT_KEY_PREFIX}${name}`;

    try {
        const loaded = await load();
        clearStorage(attemptKey);
        return loaded;
    } catch (error) {
        if (readStorage(attemptKey) !== null) {
            throw error;
        }

        writeStorage(attemptKey, "1");
        window.location.reload();

        // The document is on its way out. Resolving would flash a page built
        // from a bundle we already know is stale, and rejecting would show an
        // error for a failure that is being repaired — so Suspense keeps its
        // spinner until the reload takes the page.
        return new Promise<T>(() => undefined);
    }
}

/**
 * `lazy()` for a route, with the reload above behind it. `name` identifies
 * the route in storage, so it has to be stable across deploys — the chunk
 * hash is not, which is the whole point.
 */
export function lazyRoute<T extends RouteComponent>(
    name: string,
    load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
    return lazy(() => loadRouteModule(name, load));
}

/**
 * Whatever `lazy()` itself accepts. Read off React's own declaration rather
 * than written out, because the only type that admits every component is
 * `ComponentType<any>` — `ComponentType<unknown>` rejects any component that
 * takes props, which is most of them.
 */
type RouteComponent = Awaited<ReturnType<Parameters<typeof lazy>[0]>>["default"];
